import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  ChevronLeft, Wallet, Plus, Megaphone, Store, Sparkles, Eye, MousePointerClick,
  TrendingUp, Pause, Play, Square, BarChart3, ChevronRight, Loader2,
} from "lucide-react";
import { CampaignWizard } from "@/pages/coach/ads/CampaignWizard";
import { CampaignDetailDialog } from "@/pages/coach/ads/CampaignDetailDialog";
import { WalletTopupDialog } from "@/pages/coach/ads/WalletTopupDialog";

const STATUS_FILTERS = ["all", "running", "paused", "pending_review", "approved", "completed", "rejected"];
const STATUS_TONE = {
  pending_review: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
  running: "bg-emerald-100 text-emerald-700",
  paused: "bg-slate-200 text-slate-700",
  completed: "bg-gray-200 text-gray-600",
};

function eur(cents) { return `€${((cents || 0) / 100).toFixed(2)}`; }

export default function Ads() {
  const { user } = useAuth();
  const t = useT();
  const [params, setParams] = useSearchParams();

  const [wallet, setWallet] = useState(0);
  const [campaigns, setCampaigns] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recs, setRecs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const [wizard, setWizard] = useState(null); // { subject_type, subject_id } | {}
  const [detailId, setDetailId] = useState(null);
  const [topupOpen, setTopupOpen] = useState(false);

  const load = useCallback(async () => {
    const get = (url) => api.get(url).then((r) => r.data).catch(() => null);
    const [w, c, s, r] = await Promise.all([
      get("/campaigns/wallet"),
      get(`/campaigns/mine${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`),
      get("/campaigns/mine/summary"),
      get("/campaigns/recommend"),
    ]);
    setWallet((w || {}).balance_cents || 0);
    setCampaigns(Array.isArray(c) ? c : []);
    setSummary(s || null);
    setRecs(Array.isArray(r?.items) ? r.items : []);
  }, [statusFilter]);

  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  useEffect(() => {
    if (params.get("topup") === "success") {
      toast.success(t("ads.topup_ok"));
      params.delete("topup");
      setParams(params, { replace: true });
    }
  }, [params, setParams, t]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const doAction = async (id, action) => {
    setBusyId(id);
    try { await api.post(`/campaigns/${id}/${action}`); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="coach-ads-page">
      <Seo title={t("ads.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="ads-back">
        <ChevronLeft className="h-4 w-4" /> {t("ads.back")}
      </Link>
      <PageHeader eyebrow={t("ads.eyebrow")} title={t("ads.title")} subtitle={t("ads.subtitle")} />

      {/* Wallet */}
      <div className="flex flex-col gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="ads-wallet">
        <div>
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary"><Wallet className="h-4 w-4" /> {t("ads.wallet")}</p>
          <p className="mt-1 font-display text-3xl font-semibold text-foreground">{eur(wallet)}</p>
          <p className="text-xs text-muted-foreground">{t("ads.wallet_hint")}</p>
        </div>
        <Button className="gap-1.5 rounded-full" onClick={() => setTopupOpen(true)} data-testid="ads-topup-btn"><Plus className="h-4 w-4" /> {t("ads.topup")}</Button>
      </div>

      {/* CTAs */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <button onClick={() => setWizard({})} data-testid="ads-new-campaign" className="group flex items-center gap-3 rounded-2xl bg-primary p-4 text-left text-primary-foreground">
          <Megaphone className="h-5 w-5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">{t("ads.new_campaign")}</p>
            <p className="text-xs opacity-80">{t("ads.new_campaign_sub")}</p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
        </button>
        <Link to="/app/coach/offerings" data-testid="ads-manage-offerings" className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
          <Store className="h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-foreground">{t("ads.offerings")}</p>
            <p className="text-xs text-muted-foreground">{t("ads.offerings_sub")}</p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>

      {/* Recommendations */}
      {recs.length > 0 && (
        <section className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="ads-recommendations">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground"><Sparkles className="h-4 w-4 text-primary" /> {t("ads.reco_title")}</p>
          <p className="mb-3 mt-0.5 text-xs text-muted-foreground">{t("ads.reco_sub")}</p>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {recs.map((r) => (
              <button key={`${r.subject_type}-${r.subject_id}`} onClick={() => setWizard({ subject_type: r.subject_type, subject_id: r.subject_id })} data-testid={`ads-reco-${r.subject_id}`} className="w-56 shrink-0 rounded-xl border border-border bg-secondary/40 p-3 text-left hover:bg-accent/40">
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary">{r.subject_type === "post" ? t("ads.subject_post") : t("ads.subject_offering")}</span>
                <p className="mt-2 line-clamp-2 text-sm font-medium text-foreground">{r.title || t("ads.untitled")}</p>
                <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{t(`ads.reco_reason_${r.reason}`) || t("ads.reco_reason_default")}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary">{t("ads.reco_cta")} <ChevronRight className="h-3 w-3" /></span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Summary */}
      {summary && (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="ads-summary">
          <SummaryStat icon={Eye} label={t("ads.impressions")} value={(summary.impressions || 0).toLocaleString("de-DE")} />
          <SummaryStat icon={MousePointerClick} label={t("ads.clicks")} value={(summary.clicks || 0).toLocaleString("de-DE")} />
          <SummaryStat icon={TrendingUp} label={t("ads.ctr")} value={`${summary.ctr_pct || 0}%`} />
          <SummaryStat icon={Wallet} label={t("ads.total_spent")} value={`€${(summary.spent || 0).toFixed(2)}`} emphasis />
        </div>
      )}

      {/* Campaign list */}
      <h2 className="mb-3 mt-8 font-display text-xl text-foreground">{t("ads.my_campaigns")}</h2>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {STATUS_FILTERS.map((st) => (
          <button key={st} onClick={() => setStatusFilter(st)} data-testid={`ads-filter-${st}`} className={`rounded-full px-3 py-1.5 text-xs font-medium ${statusFilter === st ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-accent"}`}>
            {t(`ads.status_${st}`)}
          </button>
        ))}
      </div>

      {campaigns === null ? (
        <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-36 rounded-2xl" />)}</div>
      ) : campaigns.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-border py-10 text-center" data-testid="ads-empty">
          <Megaphone className="h-7 w-7 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">{t("ads.empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => {
            const spentPct = c.budget?.total_cents > 0 ? Math.min(100, Math.round((c.spent_cents / c.budget.total_cents) * 100)) : 0;
            const running = c.status === "running";
            const paused = c.status === "paused";
            const canPauseResume = running || paused;
            const canStop = c.status !== "completed" && c.status !== "rejected";
            return (
              <div key={c.id} data-testid={`ads-campaign-${c.id}`} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <button onClick={() => setDetailId(c.id)} data-testid={`ads-open-${c.id}`} className="min-w-0 flex-1 text-left">
                    <p className="line-clamp-2 font-medium text-foreground hover:text-primary">{c.subject_snapshot?.title || t("ads.untitled")}</p>
                  </button>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${STATUS_TONE[c.status] || STATUS_TONE.pending_review}`}>{t(`ads.status_${c.status}`)}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">{c.subject_type === "post" ? t("ads.subject_post") : t("ads.subject_offering")} · {t(`ads.billing_${c.billing_mode}`)}</p>

                <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                  <CampMetric label={t("ads.impressions")} value={c.impressions || 0} />
                  <CampMetric label={t("ads.clicks")} value={c.clicks || 0} />
                  <CampMetric label={t("ads.ctr")} value={`${c.impressions > 0 ? ((c.clicks / c.impressions) * 100).toFixed(1) : "0.0"}%`} />
                  <CampMetric label={t("ads.budget_left")} value={`€${((c.budget.total_cents - c.spent_cents) / 100).toFixed(0)}`} />
                </div>

                <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${spentPct}%` }} />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{t("ads.spent_of", { spent: eur(c.spent_cents), total: `€${(c.budget.total_cents / 100).toFixed(0)}` })}</p>

                <div className="mt-3 flex flex-wrap gap-2">
                  {canPauseResume && (
                    <Button size="sm" variant="outline" className="h-8 gap-1.5 rounded-full" disabled={busyId === c.id} onClick={() => doAction(c.id, paused ? "resume" : "pause")} data-testid={`ads-action-${paused ? "resume" : "pause"}-${c.id}`}>
                      {busyId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                      {paused ? t("ads.resume") : t("ads.pause")}
                    </Button>
                  )}
                  {canStop && (
                    <Button size="sm" variant="outline" className="h-8 gap-1.5 rounded-full text-rose-600 hover:text-rose-700" disabled={busyId === c.id} onClick={() => doAction(c.id, "stop")} data-testid={`ads-action-stop-${c.id}`}>
                      <Square className="h-3.5 w-3.5" /> {t("ads.stop")}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-8 gap-1.5 rounded-full text-primary" onClick={() => setDetailId(c.id)} data-testid={`ads-details-${c.id}`}>
                    <BarChart3 className="h-3.5 w-3.5" /> {t("ads.view_details")}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {wizard && <CampaignWizard open preselect={wizard} wallet={wallet} onClose={(created) => { setWizard(null); if (created) load(); }} />}
      {detailId && <CampaignDetailDialog campaignId={detailId} onClose={() => setDetailId(null)} />}
      {topupOpen && <WalletTopupDialog open balance={wallet} onClose={(ok) => { setTopupOpen(false); if (ok) load(); }} />}
    </div>
  );
}

function SummaryStat({ icon: Icon, label, value, emphasis }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <Icon className={`h-4 w-4 ${emphasis ? "text-primary" : "text-sage-light"}`} />
      <p className="mt-1 text-xs text-muted-foreground">{label}</p>
      <p className={`font-display text-lg font-semibold ${emphasis ? "text-primary" : "text-foreground"}`}>{value}</p>
    </div>
  );
}

function CampMetric({ label, value }) {
  return (
    <div>
      <p className="font-display text-base font-semibold text-foreground">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}
